import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage, request } from '../api';

interface RetentionReport {
  mode: 'delete' | 'archive';
  skipped: boolean;
  screenshotsDeleted: number;
  screenshotObjectsDeleted: number;
  orphanGenerationsDeleted: number;
  errors: string[];
}

export function Retention(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<RetentionReport | null>(null);

  const runRetention = useMutation({
    mutationFn: () => request<RetentionReport>('POST', '/admin/retention/run'),
    onSuccess: setReport,
    onError: (caught) => setError(errorMessage(caught)),
  });

  const retentionMode = useQuery({
    queryKey: ['admin', 'retention-mode'],
    queryFn: () => request<{ mode: 'delete' | 'archive' }>('GET', '/admin/retention/mode'),
  });

  const setRetentionMode = useMutation({
    mutationFn: (mode: 'delete' | 'archive') =>
      request<{ mode: string }>('POST', '/admin/retention/mode', { mode }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'retention-mode'] });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <>
      <header className="page-head">
        <h1>Retention</h1>
        <p className="muted small" style={{ margin: 0 }}>
          What happens to screenshots past the window, and the manual trigger for the nightly job.
        </p>
      </header>

      {error && <p className="error small">{error}</p>}

      <section className="card">
        <div className="row">
          <div>
            <strong>Retention</strong>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              Runs nightly. Purges screenshots past the window and generations that never became
              a bid — PDFs and bid records are kept indefinitely.
            </p>
          </div>
          <button onClick={() => runRetention.mutate()} disabled={runRetention.isPending}>
            {runRetention.isPending ? 'Running…' : 'Run now'}
          </button>
        </div>

        <div className="row" style={{ marginTop: 10, justifyContent: 'flex-start', gap: 10 }}>
          <label className="muted small" htmlFor="retention-mode">
            Expired screenshots are
          </label>
          <select
            id="retention-mode"
            value={retentionMode.data?.mode ?? 'delete'}
            disabled={retentionMode.isLoading || setRetentionMode.isPending}
            onChange={(e) => setRetentionMode.mutate(e.target.value as 'delete' | 'archive')}
          >
            <option value="delete">deleted permanently</option>
            <option value="archive">moved to the archive/ folder in storage</option>
          </select>
        </div>

        {report && (
          <p className="small" style={{ marginBottom: 0 }}>
            {report.skipped ? (
              <span className="warn">
                Skipped — another instance is running retention right now.
              </span>
            ) : (
              <>
                {report.mode === 'archive' ? 'Archived' : 'Deleted'} {report.screenshotsDeleted}{' '}
                screenshot row(s), {report.screenshotObjectsDeleted} stored object(s), and
                deleted {report.orphanGenerationsDeleted} orphan generation(s).
                {report.errors.length > 0 && (
                  <span className="warn">
                    {' '}
                    {report.errors.length} object(s) could not be processed.
                  </span>
                )}
              </>
            )}
          </p>
        )}
      </section>
    </>
  );
}
